import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { IconButton, UnstyledButton } from '../Button';
import VipIcon from '../../Icons/VipIcons';
import MediaViewer from './MediaViewer';
import { MEDIA_MODAL } from '../../../constants/staticTexts';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';

/**
 * Universal Media Modal Component
 * 
 * Features:
 * - Full-screen media viewing
 * - Navigation between multiple media items
 * - Keyboard controls (ESC, arrows, space)
 * - Touch/swipe support
 * - Auto-sizing for different media types
 * - Smooth animations
 */

const MediaModal = ({ 
  isOpen,
  media,
  onClose,
  // For carousel support
  mediaList = null,
  currentIndex = 0,
  onNavigate = null,
  onTimeUpdate = null,
}) => {
  const hasNavigation = mediaList && mediaList.length > 1 && onNavigate;
  const canGoPrevious = hasNavigation && currentIndex > 0;
  const canGoNext = hasNavigation && currentIndex < mediaList.length - 1;

  const getMediaUrl = useCallback((mediaPath) => {
    if (!mediaPath) return '';
    if (mediaPath.startsWith('http')) return mediaPath;
    return mediaPath;
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrevious = useCallback(() => {
    if (canGoPrevious) {
      onNavigate(currentIndex - 1);
    }
  }, [canGoPrevious, currentIndex, onNavigate]);

  const handleNext = useCallback(() => {
    if (canGoNext) {
      onNavigate(currentIndex + 1);
    }
  }, [canGoNext, currentIndex, onNavigate]);

  // Keyboard controls
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (canGoPrevious) handlePrevious();
          break;
        case 'ArrowRight':
          if (canGoNext) handleNext();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, canGoPrevious, canGoNext, handlePrevious, handleNext]);

  useBodyScrollLock(isOpen);

  if (!isOpen || !media) return null;

  const mediaType = media.mediaType?.toLowerCase() || media.type?.toLowerCase() || 'image';
  const mediaUrl = getMediaUrl(media.fileUrl || media.url || media.src || media.preview);
  const thumbnailUrl = media.thumbnailUrl || media.poster;
  const altText = media.name || media.alt || MEDIA_MODAL.ALT_MEDIA_VIEWER;

  const modalContent = (
    <>
      <ModalOverlay onClick={handleOverlayClick} />
      <Modal>
        {/* Close Button */}
        <CloseButton onClick={onClose} title={MEDIA_MODAL.CLOSE_BUTTON} aria-label={MEDIA_MODAL.CLOSE_BUTTON}>
          <VipIcon name="CloseXCircle" size={24} />
        </CloseButton>

        <MediaContainer>
          {/* Navigation Controls */}
          {hasNavigation && (
            <>
              <NavigationButton 
                $position="left" 
                onClick={handlePrevious}
                disabled={!canGoPrevious}
                $visible={canGoPrevious}
                title={MEDIA_MODAL.PREVIOUS_BUTTON}
              >
                <VipIcon name="ChevronLeft" size={24} />
              </NavigationButton>
              
              <NavigationButton 
                $position="right" 
                onClick={handleNext}
                disabled={!canGoNext}
                $visible={canGoNext}
                title={MEDIA_MODAL.NEXT_BUTTON}
              >
                <VipIcon name="ChevronRight" size={24} />
              </NavigationButton>
            </>
          )}

          {/* Media Content */}
          <MediaWrapper>
            <MediaViewer
              src={mediaUrl}
              type={mediaType}
              alt={altText}
              poster={thumbnailUrl}
              showControls={mediaType === 'video' || mediaType === 'audio'}
              autoPlay={media.shouldAutoPlay || false} // Use auto-play state from carousel
              startTime={media.startTime || 0} // Use start time from carousel
              onTimeUpdate={onTimeUpdate} // Expose playback position to parent
              muted={false}
              className="modal-media"
              processingStatus={media.processingState || media.processingStatus || 'completed'}
              // Pass through video quality data for multi-quality support
              qualityUrls={media.qualityUrls || {}}
              availableQualities={media.availableQualities || []}
              videoSources={media.videoSources || []}
              defaultQuality={media.defaultQuality || "720p"}
              enableQualitySelection={true} // Always enable in modal
              isFreePreview={media.isFreePreview}
              showLockIcon={false} // Disable top-right lock icon in modal (we show it in counter instead)
            />
          </MediaWrapper>

          {/* Media Counter with lock status */}
          {hasNavigation && (
            <MediaCounter>
              <CounterContent>
                <CounterText>{currentIndex + 1} {MEDIA_MODAL.COUNTER_OF} {mediaList.length}</CounterText>
                {media && media.isFreePreview !== undefined && (
                  <LockStatusIndicator $isLocked={!media.isFreePreview}>
                    {media.isFreePreview ? (
                      <VipIcon name="UnlockIcon" size={16} />
                    ) : (
                      <VipIcon name="Lock" size={16} />
                    )}
                  </LockStatusIndicator>
                )}
              </CounterContent>
            </MediaCounter>
          )}
        </MediaContainer>
      </Modal>
    </>
  );

  // Render modal using portal to ensure it's at the root level
  return createPortal(modalContent, document.body);
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 10000002;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  animation: fadeIn 0.2s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000003;
  width: 100vw;
  height: 100vh;
  height: 100dvh; /* mobile browsers: 100vh includes the area behind the URL bar and cuts off the bottom controls */
  display: flex;
  align-items: center;
  justify-content: center;
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideIn {
    from { 
      opacity: 0;
      transform: scale(0.9);
    }
    to { 
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const CloseButton = styled(IconButton).attrs({ $size: 'large' })`
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: 10000004;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    top: 15px;
    right: 15px;
  }
`;

const MediaContainer = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
`;

const MediaWrapper = styled.div`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  /* keep the video controls above the iOS home indicator */
  padding-bottom: env(safe-area-inset-bottom, 0px);
  display: flex;
  align-items: center;
  justify-content: center;
  
  .modal-media {
    width: 100vw;
    height: 100%;
    border-radius: 0;
    background: #000;
    
    /* Ensure the MediaViewer inside takes full space */
    > div {
      width: 100%;
      height: 100%;
      border-radius: 0;
    }
    
    /* Ensure images and videos fill the screen properly */
    img, video {
      max-width: 100vw;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }
  }
`;

const NavigationButton = styled(UnstyledButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $position }) => $position}: 16px;
  
  width: 48px;
  height: 48px;
  border: none;
  background: transparent; /* Remove round background */
  color: white; /* Change to white for better visibility without background */
  cursor: pointer;
  
  display: flex;
  align-items: center;
  justify-content: center;
  
  opacity: ${({ $visible }) => $visible ? 0.8 : 0.3};
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};
  
  transition: all 0.3s ease;
  z-index: 1003;
  
  /* Add shadow instead of background */
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));

  &:hover:not(:disabled) {
    color: white;
    transform: translateY(-50%) scale(1.1);
    opacity: 1;
    /* Enhanced shadow on hover */
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
  }

  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.95);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    ${({ $position }) => $position}: 8px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.9);
    }
  }
`;

/* Top-center so it never sits on top of the video controls at the bottom */
const MediaCounter = styled.div`
  position: absolute;
  top: calc(16px + env(safe-area-inset-top, 0px));
  left: 50%;
  transform: translateX(-50%);
  
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: 1003;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const CounterContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
`;

const CounterText = styled.span`
  color: white;
  font-size: inherit;
  font-weight: inherit;
`;

const LockStatusIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  
  svg {
    color: white;
  }
`;

export default MediaModal;
